import { createMemo, createSignal, For, Match, Show, Switch } from "solid-js";

import { getItem, getItemContainer, giveItem, ItemCard, itemQualities, itemRarity } from "~/js/items";
import { openContainer } from "~/js/opencase";
import { createPage, isContainerItem, isKeyItem, isMasterItem, isSkinItem, isStickerItem } from "~/js/utils";
import { getPlayerLevel, goToPreviousPage, numToValuta } from "~/script";
import type { ContainerItem, KeyItem, MasterSkinItem, SkinItem, StickerItem } from "~/types";
import { user } from "~/userStore";

const INSTANT_OPEN_FEE = 15;

const [currentItemThing, setCurrentItemThing] = createSignal<string | null>(null);

function InspectItem() {
	const isOwned = createMemo(() => currentItemThing()?.endsWith("&owned") || false);
	const itemName = createMemo(() => {
		const current = currentItemThing();
		return isOwned() && current ? current.substring(0, current.length - 6) : current;
	});
	const item = createMemo(() => {
		const item = itemName();
		return item ? getItem(item) : null;
	});

	return (
		<div>
			<Switch fallback={<div>There's no item to inspect. How did you get here ???{currentItemThing() || ""}</div>}>
				<Match when={item() && isContainerItem(item()!)}>
					<ContainerItemInspect isOwned={isOwned()} item={item() as ContainerItem} />
				</Match>
				<Match when={item() && isStickerItem(item()!)}>
					<StickerItemInspect isOwned={isOwned()} item={item() as StickerItem} />
				</Match>
				<Match when={item() && isSkinItem(item()!)}>
					<SkinItemInspect isOwned={isOwned()} item={item() as SkinItem} />
				</Match>
				<Match when={item() && isMasterItem(item()!)}>
					<MasterItemInspect isOwned={isOwned()} item={item() as MasterSkinItem} />
				</Match>
				<Match when={item() && isKeyItem(item()!)}>
					<KeyItemInspect isOwned={isOwned()} item={item() as KeyItem} />
				</Match>
			</Switch>
		</div>
	);
}
function ContainerItemInspect(props: { item: ContainerItem; isOwned: boolean }) {
	let dropdownRef: HTMLDivElement;
	const key = createMemo(() => (props.item.keyName ? (getItem(props.item.keyName) as KeyItem) : null));
	// [0] = key count, [1] = case count
	const inventoryCounts = createMemo(() => {
		return user.inventory.reduce(
			([keyAcc, caseAcc], invItem) => {
				if (invItem === props.item.keyName) keyAcc++;
				if (invItem === props.item.name) caseAcc++;
				return [keyAcc, caseAcc] as [number, number];
			},
			[0, 0] as [number, number],
		);
	});

	const instantOpenCount = createMemo(() => {
		const maxInstantOpens = user.upgrades.instantOpen === 1 ? 100 : user.upgrades.instantOpen;
		return Math.min(inventoryCounts()[1], maxInstantOpens);
	});

	const availableOpens = createMemo(() => (key() ? Math.min(inventoryCounts()[0], inventoryCounts()[1]) : inventoryCounts()[1]));
	const instantOpenCost = createMemo(() => {
		const keyItem = key();
		if (keyItem) {
			return keyItem.price * (instantOpenCount() - inventoryCounts()[0]) + instantOpenCount() * INSTANT_OPEN_FEE;
		} else {
			return instantOpenCount() * INSTANT_OPEN_FEE;
		}
	});

	const sortedItems = createMemo(() => {
		return Iterator.from(props.item.content.items)
			.map((itemName) => getItem(itemName))
			.filter((item) => isMasterItem(item) || isStickerItem(item))
			.filter((item) => item.rarity !== 6)
			.toArray()
			.sort((a, b) => {
				if (a.rarity > b.rarity) return -1;
				if (a.rarity < b.rarity) return 1;
				if (a.name > b.name) return -1;
				if (a.name < b.name) return 1;
				return 0;
			});
	});

	const hasSpecialItem = createMemo(() => {
		return props.item.content.items.some((itemName) => getItem(itemName).rarity === 6);
	});

	return (
		<div>
			<div class="inspect-top">
				<button class="button back-btn" onclick={() => goToPreviousPage()} type="button">
					Back
				</button>
				<div class="inspect-title" style={{ color: itemRarity[props.item.rarity].color }}>
					{props.item.name}
				</div>
			</div>

			<div class="center">
				<div class="inspect-image mini" style={{ "background-image": `url(${props.item.src})` }} />
			</div>
			<div class="inspect-case show">
				<div class="inspect-case-wheel" />
				<div class="inspect-case-options">
					<button class="button" disabled={availableOpens() < 1} onclick={() => openContainer(props.item.name, 1, false)} type="button">
						Open
					</button>
					<button class="button" disabled={availableOpens() < 2} onclick={() => dropdownRef.classList.add("show")} type="button">
						+
					</button>
					<div
						class="dropdown"
						ref={(el) => {
							dropdownRef = el;
						}}
					>
						<div class="dropdown-text">Open</div>
						<div class="flex">
							<button class="button" disabled={availableOpens() < 2} onclick={() => openContainer(props.item.name, 2, false)} type="button">
								2
							</button>
							<button class="button" disabled={availableOpens() < 3} onclick={() => openContainer(props.item.name, 3, false)} type="button">
								3
							</button>
							<button class="button" disabled={availableOpens() < 4} onclick={() => openContainer(props.item.name, 4, false)} type="button">
								4
							</button>
							<button class="button" disabled={availableOpens() < 5} onclick={() => openContainer(props.item.name, 5, false)} type="button">
								5
							</button>
						</div>
						<div class="dropdown-text">Cases</div>
					</div>
					<Show when={props.item.keyName}>
						<button
							class="button"
							disabled={key() ? user.money < key()!.price : true}
							onclick={() => {
								if (user.money >= (key()?.price || Number.MAX_VALUE)) {
									user.money -= key()!.price;
									giveItem(props.item.keyName!);
								}
							}}
							type="button"
						>
							Buy key for {numToValuta(key()!.price, 2)}
						</button>
						<button
							class="button"
							disabled={key() ? user.money < key()!.price : true}
							onclick={() => {
								if (user.money >= (key()?.price || Number.MAX_VALUE) * 5) {
									user.money -= key()!.price * 5;
									giveItem(props.item.keyName!, 5);
								}
							}}
							type="button"
						>
							5x for Buy key for {numToValuta(key()!.price * 5, 2)}
						</button>
					</Show>
					<div style="width: 20px;"></div>
					<Switch>
						<Match when={!user.upgrades.instantOpen}>
							<button class="button" disabled type="button">
								"Open cases instant (Buy Instant Open Upgrade to unlock!)"
							</button>
						</Match>
						<Match when={key() && inventoryCounts()[0] < instantOpenCount()}>
							<button
								class="button"
								disabled={user.money < instantOpenCost()}
								onclick={() => {
									if (getPlayerLevel().level >= 10 && user.money >= instantOpenCost()) {
										user.money -= instantOpenCost();

										giveItem(props.item.keyName!, instantOpenCount() - inventoryCounts()[0]);

										openContainer(props.item.name, instantOpenCount(), true);
									}
								}}
								type="button"
							>
								Open {instantOpenCount()} case{instantOpenCount() !== 1 ? "s" : ""} instant for {numToValuta(instantOpenCost(), 2)} (will
								buy {instantOpenCount() - inventoryCounts()[0]} keys + {numToValuta(instantOpenCount() * INSTANT_OPEN_FEE, 2)} service fee)
							</button>
						</Match>
						<Match when={key() === null}>
							<button
								class="button"
								disabled={user.money < instantOpenCost()}
								onclick={() => {
									if (getPlayerLevel().level >= 10 && user.money >= instantOpenCost()) {
										user.money -= instantOpenCost();

										openContainer(props.item.name, instantOpenCount(), true);
									}
								}}
								type="button"
							>
								Open {instantOpenCount()} case{instantOpenCount() !== 1 ? "s" : ""} instant for {numToValuta(instantOpenCost(), 2)}
							</button>
						</Match>
					</Switch>
				</div>
				<div class="inspect-case-content">
					{hasSpecialItem() && (
						<div class="item-container">
							<div class="item" style="border-color: rgb(255, 221, 0);">
								<div class="item-image" style={{ "background-image": `url(/images/special-item.png)` }} />
								<div class="item-text">Special Item</div>
							</div>
						</div>
					)}
					<For each={sortedItems()}>{(item) => <ItemCard item={item} />}</For>
				</div>
			</div>
		</div>
	);
}

function StickerItemInspect(props: { item: StickerItem; isOwned: boolean }) {
	const containers = () => getItemContainer(props.item.name);

	return (
		<div>
			<div class="inspect-top">
				<button class="button back-btn" onclick={() => goToPreviousPage()} type="button">
					Back
				</button>
				<div class="inspect-title" style={{ color: itemRarity[props.item.rarity].color }}>
					{props.item.name}
				</div>
			</div>

			<div class="center">
				<div class="inspect-image" style={{ "background-image": `url(${props.item.src})` }} />
			</div>

			<div class="center">
				<table class="inspect-item-stats">
					<tbody>
						<tr>
							<th>Sticker Name</th>
							<td>{props.item.name}</td>
						</tr>
						<tr>
							<th>Capsule{containers().length !== 1 ? "s" : ""}</th>
							<td>
								{containers().length > 0
									? containers()
											.map((c) => c.name)
											.join(", ")
									: "Not available in capsules"}
							</td>
						</tr>
						<tr>
							<th>Value</th>
							<td>{numToValuta(props.item.price, 2)}</td>
						</tr>
						<tr>
							<th>Rarity</th>
							<td style={{ color: itemRarity[props.item.rarity].color }}>{itemRarity[props.item.rarity].display}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}

function MasterItemInspect(props: { item: MasterSkinItem; isOwned: boolean }) {
	return (
		<div>
			<div class="inspect-top">
				<button class="button back-btn" onclick={() => goToPreviousPage()} type="button">
					Back
				</button>
				<div class="inspect-title" style={{ color: itemRarity[props.item.rarity].color }}>
					{props.item.name}
				</div>
			</div>

			<div class="center">
				<div class="inspect-image" style={{ "background-image": `url(${props.item.src})` }} />
			</div>

			<div class="center">
				<table class="inspect-item-stats">
					<tbody>
						<tr>
							<th>Skin Name</th>
							<td>{props.item.skin}</td>
						</tr>
						<tr>
							<th>Skin Weapon</th>
							<td>{props.item.weapon}</td>
						</tr>
						<tr>
							<th>Highest Value</th>
							<td>{numToValuta(props.item.price, 2)}</td>
						</tr>
						<tr>
							<th>Skin Weapon</th>
							<td>{numToValuta(props.item.minPrice, 2)}</td>
						</tr>
						<tr>
							<th>Rarity</th>
							<td style={{ color: itemRarity[props.item.rarity].color }}>{itemRarity[props.item.rarity].display}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}

function KeyItemInspect(props: { item: KeyItem; isOwned: boolean }) {
	return (
		<div>
			<div class="inspect-top">
				<button class="button back-btn" onclick={() => goToPreviousPage()} type="button">
					Back
				</button>
				<div class="inspect-title" style={{ color: itemRarity[props.item.rarity].color }}>
					{props.item.name}
				</div>
			</div>

			<div class="center">
				<div class="inspect-image" style={{ "background-image": `url(${props.item.src})` }} />
			</div>

			<div class="center">
				<table class="inspect-item-stats">
					<tbody>
						<tr>
							<th>Value</th>
							<td>{numToValuta(props.item.price, 2)}</td>
						</tr>
						<tr>
							<th>Rarity</th>
							<td style={{ color: itemRarity[props.item.rarity].color }}>{itemRarity[props.item.rarity].display}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}

function SkinItemInspect(props: { item: SkinItem; isOwned: boolean }) {
	const containers = createMemo(() => getItemContainer(props.item.name));

	return (
		<div>
			<div class="inspect-top">
				<button class="button back-btn" onclick={() => goToPreviousPage()} type="button">
					Back
				</button>
				<div class="inspect-title" style={{ color: itemRarity[props.item.rarity].color }}>
					{props.item.name}
				</div>
			</div>

			<div class="center">
				<div class="inspect-image" style={{ "background-image": `url(${props.item.src})` }} />
			</div>

			<div class="center">
				<table class="inspect-item-stats">
					<tbody>
						<tr>
							<th>Skin Name</th>
							<td>{props.item.skin}</td>
						</tr>
						<tr>
							<th>Skin Weapon</th>
							<td>{props.item.weapon}</td>
						</tr>
						<tr>
							<th>Container{containers().length > 1 ? "s" : ""}</th>
							<td>
								{containers().length > 0
									? containers()
											.map((c) => c.name)
											.join(", ")
									: "Not available in cases"}
							</td>
						</tr>
						<tr>
							<th>Value</th>
							<td>{numToValuta(props.item.price, 2)}</td>
						</tr>
						<tr>
							<th>Rarity</th>
							<td style={{ color: itemRarity[props.item.rarity].color }}>{itemRarity[props.item.rarity].display}</td>
						</tr>
						<tr>
							<th>Quality</th>
							<td>{props.item.quality ? itemQualities[props.item.quality] : "Vanilla"}</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}

createPage(
	() => <InspectItem />,
	"inspect",
	(p) => {
		if (p.page === "inspect") setCurrentItemThing(p.data);
	},
);
